/**
 * Typed wrappers for the Accounting module (G_ACC3a — contacts + items
 * master CRUD).
 *
 * Paths and shapes mirror backend/internal/router/router.go and the
 * handler routes in accounting_contacts.go + accounting_items.go.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). Every /accounting
 * route is:
 *   authMW → RequireFeature("accounting") → accountingRole → handler
 * where accountingRole = RequireRole(company_admin, hr_manager,
 * accountant). The frontend role-gate mirrors that set; the backend is
 * the real boundary and re-checks every request.
 *
 * No client-supplied tenant_id is ever sent: the server derives the
 * tenant from the session.
 *
 * Wire sentinels worth handling specially:
 *   - POST /items 409 code_taken — field-mapped to `code`
 *   - PATCH /items/{id} 409 code_taken — field-mapped to `code`
 *   - any 404 not_found — surface the message; do not invent existence
 *   - 422 validation — field-mapped via the standard `{ error, message,
 *     field }` envelope (handleAuthError)
 */

import { api, API_BASE_URL, blobErrorToApi } from "./client";
import { namedBlob } from "@/lib/download-filename";
import type {
  Account,
  AccountType,
  BalanceSheetResult,
  Bill,
  CreatePeriodLockRequest,
  CreateTaxCodeRequest,
  DashboardWidget,
  GstProfile,
  UpdateGstProfileRequest,
  TaxReturnPreview,
  BillTDSPreview,
  TaxReturnType,
  AccountLedger,
  ListPayrollRegisterResponse,
  PayrollRegisterRun,
  ListAuditEventsParams,
  ListAuditEventsResponse,
  ListPeriodLocksResponse,
  ListTaxCodesResponse,
  MSMEOverdueResult,
  PeriodLock,
  ProfitAndLossResult,
  TaxCode,
  TaxCodeKind,
  TDSReceivableResult,
  TrialBalanceResult,
  UpdateTaxCodeRequest,
  BillStatus,
  Contact,
  ContactKind,
  CreateBillRequest,
  CreateContactRequest,
  CreateInvoiceRequest,
  CreateItemRequest,
  CreatePaymentAccountRequest,
  CreatePaymentRequest,
  CreateQuoteRequest,
  CreateReceiptRequest,
  Invoice,
  InvoiceEmailResponse,
  InvoiceStatus,
  Item,
  ItemKind,
  ListAccountsResponse,
  ListBillsResponse,
  ListContactsResponse,
  ListInvoicesResponse,
  ListItemsResponse,
  ListPaymentAccountsResponse,
  ListPaymentsResponse,
  ListQuotesResponse,
  ListReceiptsResponse,
  ListTallyMappingsResponse,
  TallyMappingRow,
  UpsertTallyMappingRequest,
  Payment,
  PaymentAccount,
  PaymentAccountKind,
  PaymentStatus,
  PostedPayment,
  PostedReceipt,
  Quote,
  QuoteEmailResponse,
  QuoteStatus,
  Receipt,
  ReceiptStatus,
  TransitionQuoteRequest,
  UpdateBillRequest,
  UpdateContactRequest,
  UpdateInvoiceRequest,
  UpdateItemRequest,
  UpdatePaymentAccountRequest,
  UpdatePaymentRequest,
  UpdateQuoteRequest,
  UpdateReceiptRequest,
} from "@/types/accounting";

export const ACCOUNTING_BASE = "/api/v1/accounting";

// ---------------------------------------------------------------------------
// Contacts (5 endpoints)
// ---------------------------------------------------------------------------

/**
 * GET /accounting/contacts[?kind=&search=&gstin=&include_archived=]
 *
 * Tenant-scoped via session; the role gate is identical to the writes
 * (company_admin / hr_manager / accountant). Default response excludes
 * archived rows; pass `include_archived: true` to widen.
 */
export async function listContacts(
  params?: {
    kind?: ContactKind;
    search?: string;
    gstin?: string;
    include_archived?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListContactsResponse> {
  const { data } = await api.get<ListContactsResponse>(
    `${ACCOUNTING_BASE}/contacts`,
    {
      params: {
        ...(params?.kind ? { kind: params.kind } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.gstin ? { gstin: params.gstin } : {}),
        ...(params?.include_archived ? { include_archived: "true" } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * POST /accounting/contacts — create a contact. Backend writes the
 * master row + a master_create audit_events row in one TX (Rule 3(1)).
 *
 * Wire errors:
 *   - 422 validation — `{ field: "kind" | "name" | "gstin" | ... }`
 *   - 400 invalid_payload — malformed JSON
 *   - 403 tenant_required — caller has no active tenant
 *   - 403 forbidden — caller's role is not HR-admin or accountant
 */
export async function createContact(
  body: CreateContactRequest,
): Promise<Contact> {
  const { data } = await api.post<Contact>(
    `${ACCOUNTING_BASE}/contacts`,
    body,
  );
  return data;
}

/**
 * GET /accounting/contacts/{id} — load one contact. Cross-tenant id is
 * 404 not_found (no oracle).
 */
export async function getContact(
  id: number,
  signal?: AbortSignal,
): Promise<Contact> {
  const { data } = await api.get<Contact>(
    `${ACCOUNTING_BASE}/contacts/${id}`,
    { signal },
  );
  return data;
}

/**
 * PATCH /accounting/contacts/{id} — partial update. Sends only the
 * fields the form actually changed; the handler applies them and
 * writes a master_update audit_events row with before+after snapshots
 * in the same TX.
 */
export async function updateContact(
  id: number,
  body: UpdateContactRequest,
): Promise<Contact> {
  const { data } = await api.patch<Contact>(
    `${ACCOUNTING_BASE}/contacts/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/contacts/{id}/archive — soft-archive (no hard delete
 * per Companies Act §128(5) 8-year retention). Returns the updated
 * row with `is_archived: true`. Idempotent at the wire — a second call
 * is a no-op (the handler returns the already-archived row).
 */
export async function archiveContact(id: number): Promise<Contact> {
  const { data } = await api.post<Contact>(
    `${ACCOUNTING_BASE}/contacts/${id}/archive`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Items (5 endpoints)
// ---------------------------------------------------------------------------

/**
 * GET /accounting/items[?kind=&search=&include_archived=]
 */
export async function listItems(
  params?: {
    kind?: ItemKind;
    search?: string;
    include_archived?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListItemsResponse> {
  const { data } = await api.get<ListItemsResponse>(
    `${ACCOUNTING_BASE}/items`,
    {
      params: {
        ...(params?.kind ? { kind: params.kind } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(params?.include_archived ? { include_archived: "true" } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * POST /accounting/items — create an item.
 *
 * Wire errors (caller maps to field-level via handleAuthError):
 *   - 422 validation — `{ field: "code" | "name" | "kind" |
 *     "default_tax_rate_bps" | "hsn_sac" | ... }`
 *   - 409 code_taken — `{ field: "code" }` (per-tenant UNIQUE)
 *   - 422 validation — referenced default_*_account_id not in tenant
 */
export async function createItem(
  body: CreateItemRequest,
): Promise<Item> {
  const { data } = await api.post<Item>(
    `${ACCOUNTING_BASE}/items`,
    body,
  );
  return data;
}

/**
 * GET /accounting/items/{id} — load one item. Cross-tenant id is 404.
 */
export async function getItem(
  id: number,
  signal?: AbortSignal,
): Promise<Item> {
  const { data } = await api.get<Item>(
    `${ACCOUNTING_BASE}/items/${id}`,
    { signal },
  );
  return data;
}

/**
 * PATCH /accounting/items/{id} — partial update.
 *
 * Kind is intentionally NOT in the update wire — item kind is set at
 * create time and the handler refuses any later change.
 *
 * Wire errors:
 *   - 409 code_taken — `{ field: "code" }`
 *   - 422 validation — bps out of range, hsn/sac too long, etc.
 */
export async function updateItem(
  id: number,
  body: UpdateItemRequest,
): Promise<Item> {
  const { data } = await api.patch<Item>(
    `${ACCOUNTING_BASE}/items/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/items/{id}/archive — soft-archive. Same retention
 * semantics as contacts; idempotent.
 */
export async function archiveItem(id: number): Promise<Item> {
  const { data } = await api.post<Item>(
    `${ACCOUNTING_BASE}/items/${id}/archive`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Accounts (G_ACC5 — minimal list for the line editor's account picker)
// ---------------------------------------------------------------------------

/**
 * GET /accounting/accounts[?type=&include_archived=]
 *
 * Returns the per-tenant chart of accounts. Filtered by type=income for
 * sales-line revenue heads and type=expense for bill-line expense
 * heads. The v1 set is the 20 seeded system accounts (full custom CRUD
 * is G_ACC2).
 */
export async function listAccounts(
  params?: { type?: AccountType; include_archived?: boolean },
  signal?: AbortSignal,
): Promise<ListAccountsResponse> {
  const { data } = await api.get<ListAccountsResponse>(
    `${ACCOUNTING_BASE}/accounts`,
    {
      params: {
        ...(params?.type ? { type: params.type } : {}),
        ...(params?.include_archived ? { include_archived: "true" } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * GET /accounting/accounts-with-balances[?include_archived=true]
 *
 * Browsable Chart of Accounts WITH running ledger balances (Sprint 4
 * §A). Each row carries debit_minor / credit_minor / net_minor in
 * addition to the base account fields; net_minor is signed by the
 * account's normal balance. Default excludes archived rows.
 */
export async function getAccountsWithBalances(
  params?: { include_archived?: boolean },
  signal?: AbortSignal,
): Promise<import("@/types/accounting").ListAccountsWithBalancesResponse> {
  const { data } = await api.get<
    import("@/types/accounting").ListAccountsWithBalancesResponse
  >(`${ACCOUNTING_BASE}/accounts-with-balances`, {
    params: {
      ...(params?.include_archived ? { include_archived: "true" } : {}),
    },
    signal,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Accounts CRUD (G_ACC2 — full CoA create / get / update / archive)
// ---------------------------------------------------------------------------

export interface CreateAccountRequest {
  code: string;
  name: string;
  type: AccountType;
  parent_id?: number | null;
  description?: string | null;
}

export interface UpdateAccountRequest {
  name?: string;
  description?: string | null;
}

/** GET /accounting/accounts/{id} */
export async function getAccount(
  id: number,
  signal?: AbortSignal,
): Promise<Account> {
  const { data } = await api.get<Account>(
    `${ACCOUNTING_BASE}/accounts/${id}`,
    { signal },
  );
  return data;
}

/** POST /accounting/accounts */
export async function createAccount(
  body: CreateAccountRequest,
): Promise<Account> {
  const { data } = await api.post<Account>(
    `${ACCOUNTING_BASE}/accounts`,
    body,
  );
  return data;
}

/** PATCH /accounting/accounts/{id} */
export async function updateAccount(
  id: number,
  body: UpdateAccountRequest,
): Promise<Account> {
  const { data } = await api.patch<Account>(
    `${ACCOUNTING_BASE}/accounts/${id}`,
    body,
  );
  return data;
}

/** POST /accounting/accounts/{id}/archive */
export async function archiveAccount(id: number): Promise<Account> {
  const { data } = await api.post<Account>(
    `${ACCOUNTING_BASE}/accounts/${id}/archive`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Invoices (6 endpoints) — G_ACC4b backend.
// ---------------------------------------------------------------------------

/**
 * GET /accounting/invoices[?status=&contact_id=&date_from=&date_to=]
 */
export async function listInvoices(
  params?: {
    status?: InvoiceStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  },
  signal?: AbortSignal,
): Promise<ListInvoicesResponse> {
  const { data } = await api.get<ListInvoicesResponse>(
    `${ACCOUNTING_BASE}/invoices`,
    {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contact_id ? { contact_id: params.contact_id } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * POST /accounting/invoices — create a draft invoice. Backend allocates
 * the per-tenant per-FY invoice_no (CGST §31 + Rule 46). No journal
 * effect until /send.
 *
 * Wire errors (caller maps via handleAuthError):
 *   - 422 validation — field mapped to "contact_id" / "date" /
 *     "due_date" / "lines[i].description" / "lines[i].account_id" / ...
 *   - 403 forbidden — role not in ACCOUNTING_ADMINS
 */
export async function createInvoice(
  body: CreateInvoiceRequest,
): Promise<Invoice> {
  const { data } = await api.post<Invoice>(
    `${ACCOUNTING_BASE}/invoices`,
    body,
  );
  return data;
}

/**
 * GET /accounting/invoices/{id}
 */
export async function getInvoice(
  id: number,
  signal?: AbortSignal,
): Promise<Invoice> {
  const { data } = await api.get<Invoice>(
    `${ACCOUNTING_BASE}/invoices/${id}`,
    { signal },
  );
  return data;
}

/**
 * PATCH /accounting/invoices/{id} — DRAFT only.
 *
 * Wire errors:
 *   - 409 not_draft — invoice has already been sent or cancelled
 *   - 422 validation — field-mapped
 */
export async function updateInvoice(
  id: number,
  body: UpdateInvoiceRequest,
): Promise<Invoice> {
  const { data } = await api.patch<Invoice>(
    `${ACCOUNTING_BASE}/invoices/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/invoices/{id}/send — POSTS the invoice to the books
 * (draft → sent + journal post, Companies Act §128(1) audit trail). The
 * route keeps its historical name; it emails nothing — the UI calls it
 * "Post to books", and emailing is emailInvoice below. Irreversible — the
 * engine writes journal_entries + journal_lines + audit_events in one TX.
 *
 * Wire errors:
 *   - 409 not_draft
 *   - 422 unbalanced — engine refused the post (defensive)
 *   - 422 invalid_account — system account not configured (boot seed)
 */
export async function sendInvoice(id: number): Promise<Invoice> {
  const { data } = await api.post<Invoice>(
    `${ACCOUNTING_BASE}/invoices/${id}/send`,
    {},
  );
  return data;
}

/**
 * POST /accounting/invoices/{id}/email — emails the invoice PDF to the
 * customer contact's stored email address and records who / when.
 *
 * Wire errors:
 *   - 409 invoice_not_posted / invoice_cancelled
 *   - 422 contact_email_missing / contact_email_invalid
 *   - 429 invoice_recently_emailed — double-click cooldown
 *   - 503 email_not_configured — this server cannot send email
 *   - 502 invoice_email_failed — the mail relay refused / timed out
 */
export async function emailInvoice(id: number): Promise<InvoiceEmailResponse> {
  const { data } = await api.post<InvoiceEmailResponse>(
    `${ACCOUNTING_BASE}/invoices/${id}/email`,
    {},
  );
  return data;
}

/**
 * POST /accounting/invoices/{id}/cancel
 * Cancel from draft is pure (no journal). Cancel from sent / partly_paid
 * triggers engine.Void to reverse the original entry. Cancel from paid
 * or cancelled is refused.
 *
 * Wire errors:
 *   - 409 cannot_cancel — invoice is fully paid: void the receipt(s)
 *     that paid it first (Lite books has no credit notes)
 *   - 409 invoice_has_receipts — receipts still allocated
 *   - 409 already_cancelled
 */
export async function cancelInvoice(
  id: number,
  reason?: string,
): Promise<Invoice> {
  const body = reason ? { reason } : {};
  const { data } = await api.post<Invoice>(
    `${ACCOUNTING_BASE}/invoices/${id}/cancel`,
    body,
  );
  return data;
}

/**
 * GET /accounting/invoices/{id}/pdf?download=1 — invoice PDF (Sprint 4
 * §B). Streams an application/pdf attachment from the LOCKED journal
 * entry; only valid for a POSTED invoice (a draft has no journal entry,
 * the server 422s). Returns a Blob carrying the server's
 * Content-Disposition filename (namedBlob — a blob: URL has no headers,
 * so an anchor without a download name navigated the tab to the PDF;
 * audit Q27); the caller saves it with saveBlobAs and a fallback name.
 * blobErrorToApi rehydrates the JSON error envelope so a 422/404 keeps
 * its sentinel code instead of degrading to a generic blob-parse message.
 */
export async function downloadInvoicePDF(id: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(
      `${ACCOUNTING_BASE}/invoices/${id}/pdf`,
      {
        params: { download: 1 },
        responseType: "blob",
      },
    );
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /accounting/bills/{id}/pdf?download=1 — the purchase-bill PDF (any
 * status; the server names the file). Same blob + error-envelope handling
 * as downloadInvoicePDF.
 */
export async function downloadBillPDF(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${ACCOUNTING_BASE}/bills/${id}/pdf`, {
      params: { download: 1 },
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// ---------------------------------------------------------------------------
// Quotes (6 endpoints) — G_ACC4b backend.
// ---------------------------------------------------------------------------

/**
 * GET /accounting/quotes[?status=&contact_id=]
 */
export async function listQuotes(
  params?: { status?: QuoteStatus; contact_id?: number },
  signal?: AbortSignal,
): Promise<ListQuotesResponse> {
  const { data } = await api.get<ListQuotesResponse>(
    `${ACCOUNTING_BASE}/quotes`,
    {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contact_id ? { contact_id: params.contact_id } : {}),
      },
      signal,
    },
  );
  return data;
}

/** POST /accounting/quotes — create a draft quote. */
export async function createQuote(body: CreateQuoteRequest): Promise<Quote> {
  const { data } = await api.post<Quote>(
    `${ACCOUNTING_BASE}/quotes`,
    body,
  );
  return data;
}

/** GET /accounting/quotes/{id} */
export async function getQuote(
  id: number,
  signal?: AbortSignal,
): Promise<Quote> {
  const { data } = await api.get<Quote>(
    `${ACCOUNTING_BASE}/quotes/${id}`,
    { signal },
  );
  return data;
}

/** PATCH /accounting/quotes/{id} — DRAFT only. */
export async function updateQuote(
  id: number,
  body: UpdateQuoteRequest,
): Promise<Quote> {
  const { data } = await api.patch<Quote>(
    `${ACCOUNTING_BASE}/quotes/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/quotes/{id}/transition — pure state machine.
 *
 * Accepted "to" values: sent / accepted / declined / expired.
 * For accepted→converted use the separate /convert endpoint (creates
 * a child draft invoice in the same TX).
 *
 * Wire errors:
 *   - 409 invalid_transition — not a legal hop from current status
 *   - 400 wrong_endpoint — caller passed to: 'converted' (use /convert)
 */
export async function transitionQuote(
  id: number,
  body: TransitionQuoteRequest,
): Promise<Quote> {
  const { data } = await api.post<Quote>(
    `${ACCOUNTING_BASE}/quotes/${id}/transition`,
    body,
  );
  return data;
}

/**
 * POST /accounting/quotes/{id}/convert — accepted → converted plus
 * creates a child draft invoice. The invoice is NOT yet posted; the
 * caller still has to /invoices/{id}/send to write to the journal.
 *
 * Wire errors:
 *   - 409 invalid_transition — quote is not in 'accepted' status
 *   - 409 already_converted
 *   - 422 validation — a quote line is missing a revenue account
 */
export async function convertQuote(id: number): Promise<Quote> {
  const { data } = await api.post<Quote>(
    `${ACCOUNTING_BASE}/quotes/${id}/convert`,
    {},
  );
  return data;
}

/**
 * POST /accounting/quotes/{id}/email — emails the quotation PDF to the
 * customer contact's stored email address and records who / when. A
 * DRAFT quote that is emailed becomes "sent" server-side.
 *
 * Wire errors (messages are user-ready):
 *   - 409 quote_not_sendable — declined / expired / converted
 *   - 422 quote_validity_lapsed — "valid until" has passed
 *   - 422 contact_email_missing / contact_email_invalid
 *   - 429 quote_recently_emailed — double-click cooldown
 *   - 503 email_not_configured — this server cannot send email
 *   - 502 quote_email_failed — the mail relay refused / timed out
 */
export async function emailQuote(id: number): Promise<QuoteEmailResponse> {
  const { data } = await api.post<QuoteEmailResponse>(
    `${ACCOUNTING_BASE}/quotes/${id}/email`,
    {},
  );
  return data;
}

/**
 * GET /accounting/quotes/{id}/pdf?download=1 — the quotation PDF (any
 * status; a quote has no journal entry). Same blob pattern as
 * downloadInvoicePDF; blobErrorToApi keeps the server's sentinel.
 */
export async function downloadQuotePDF(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${ACCOUNTING_BASE}/quotes/${id}/pdf`,
      {
        params: { download: 1 },
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// ---------------------------------------------------------------------------
// Bills (6 endpoints) — G_ACC4b backend.
// ---------------------------------------------------------------------------

/**
 * GET /accounting/bills[?status=&contact_id=&date_from=&date_to=]
 */
export async function listBills(
  params?: {
    status?: BillStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  },
  signal?: AbortSignal,
): Promise<ListBillsResponse> {
  const { data } = await api.get<ListBillsResponse>(
    `${ACCOUNTING_BASE}/bills`,
    {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contact_id ? { contact_id: params.contact_id } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
      },
      signal,
    },
  );
  return data;
}

/** POST /accounting/bills — create a draft bill. */
export async function createBill(body: CreateBillRequest): Promise<Bill> {
  const { data } = await api.post<Bill>(
    `${ACCOUNTING_BASE}/bills`,
    body,
  );
  return data;
}

/** GET /accounting/bills/{id} */
export async function getBill(
  id: number,
  signal?: AbortSignal,
): Promise<Bill> {
  const { data } = await api.get<Bill>(
    `${ACCOUNTING_BASE}/bills/${id}`,
    { signal },
  );
  return data;
}

/** PATCH /accounting/bills/{id} — DRAFT only. */
export async function updateBill(
  id: number,
  body: UpdateBillRequest,
): Promise<Bill> {
  const { data } = await api.patch<Bill>(
    `${ACCOUNTING_BASE}/bills/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/bills/{id}/receive — draft → received + journal post
 * (AP direction). Mirrors invoices/send semantics.
 */
export async function receiveBill(id: number): Promise<Bill> {
  const { data } = await api.post<Bill>(
    `${ACCOUNTING_BASE}/bills/${id}/receive`,
    {},
  );
  return data;
}

/** POST /accounting/bills/{id}/cancel — mirrors invoices/cancel. */
export async function cancelBill(
  id: number,
  reason?: string,
): Promise<Bill> {
  const body = reason ? { reason } : {};
  const { data } = await api.post<Bill>(
    `${ACCOUNTING_BASE}/bills/${id}/cancel`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// G_ACC6c — Payment accounts (5 endpoints).
// ---------------------------------------------------------------------------

/** GET /accounting/payment-accounts[?kind=&include_inactive=true] */
export async function listPaymentAccounts(
  params?: {
    kind?: PaymentAccountKind;
    include_inactive?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListPaymentAccountsResponse> {
  const { data } = await api.get<ListPaymentAccountsResponse>(
    `${ACCOUNTING_BASE}/payment-accounts`,
    {
      params: {
        ...(params?.kind ? { kind: params.kind } : {}),
        ...(params?.include_inactive ? { include_inactive: "true" } : {}),
      },
      signal,
    },
  );
  return data;
}

/** POST /accounting/payment-accounts — create a wallet. */
export async function createPaymentAccount(
  body: CreatePaymentAccountRequest,
): Promise<PaymentAccount> {
  const { data } = await api.post<PaymentAccount>(
    `${ACCOUNTING_BASE}/payment-accounts`,
    body,
  );
  return data;
}

/** GET /accounting/payment-accounts/{id} */
export async function getPaymentAccount(
  id: number,
  signal?: AbortSignal,
): Promise<PaymentAccount> {
  const { data } = await api.get<PaymentAccount>(
    `${ACCOUNTING_BASE}/payment-accounts/${id}`,
    { signal },
  );
  return data;
}

/** PATCH /accounting/payment-accounts/{id} — partial update. */
export async function updatePaymentAccount(
  id: number,
  body: UpdatePaymentAccountRequest,
): Promise<PaymentAccount> {
  const { data } = await api.patch<PaymentAccount>(
    `${ACCOUNTING_BASE}/payment-accounts/${id}`,
    body,
  );
  return data;
}

/** POST /accounting/payment-accounts/{id}/archive — soft archive. */
export async function archivePaymentAccount(
  id: number,
): Promise<PaymentAccount> {
  const { data } = await api.post<PaymentAccount>(
    `${ACCOUNTING_BASE}/payment-accounts/${id}/archive`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// G_ACC6c — Receipts (6 endpoints).
// ---------------------------------------------------------------------------

/** GET /accounting/receipts[?status=&contact_id=&date_from=&date_to=] */
export async function listReceipts(
  params?: {
    status?: ReceiptStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  },
  signal?: AbortSignal,
): Promise<ListReceiptsResponse> {
  const { data } = await api.get<ListReceiptsResponse>(
    `${ACCOUNTING_BASE}/receipts`,
    {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contact_id ? { contact_id: params.contact_id } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
      },
      signal,
    },
  );
  return data;
}

/** POST /accounting/receipts — create a draft receipt with allocations. */
export async function createReceipt(
  body: CreateReceiptRequest,
): Promise<Receipt> {
  const { data } = await api.post<Receipt>(
    `${ACCOUNTING_BASE}/receipts`,
    body,
  );
  return data;
}

/** GET /accounting/receipts/{id} */
export async function getReceipt(
  id: number,
  signal?: AbortSignal,
): Promise<Receipt> {
  const { data } = await api.get<Receipt>(
    `${ACCOUNTING_BASE}/receipts/${id}`,
    { signal },
  );
  return data;
}

/** PATCH /accounting/receipts/{id} — DRAFT only. */
export async function updateReceipt(
  id: number,
  body: UpdateReceiptRequest,
): Promise<Receipt> {
  const { data } = await api.patch<Receipt>(
    `${ACCOUNTING_BASE}/receipts/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/receipts/{id}/post — irreversible. Engine writes
 * Dr wallet / Cr AR (one credit line per allocation), bumps every
 * invoice.paid_minor, and flips receipt.status='posted'. Companies
 * Act §128(1) audit trail.
 *
 * Wire errors:
 *   - 409 receipt_not_draft
 *   - 422 overpayment_unsupported — sum(allocations) != amount_minor
 *   - 422 invoice_not_allocable — bad invoice id / status / remaining
 *   - 422 period_locked — Companies Act §128(5) period guard
 */
export async function postReceipt(id: number): Promise<PostedReceipt> {
  const { data } = await api.post<PostedReceipt>(
    `${ACCOUNTING_BASE}/receipts/${id}/post`,
    {},
  );
  return data;
}

/**
 * POST /accounting/receipts/{id}/void — only from posted. Engine writes
 * a reversing journal entry and rolls back every invoice.paid_minor
 * bump in the same TX. Companies Act §128(5) preserves the original
 * row (status='cancelled' + reversing JE) — never hard-delete.
 */
export async function voidReceipt(id: number): Promise<Receipt> {
  const { data } = await api.post<Receipt>(
    `${ACCOUNTING_BASE}/receipts/${id}/void`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// G_ACC6c — Payments (6 endpoints) — AP mirror with TDS support.
// ---------------------------------------------------------------------------

/** GET /accounting/payments[?status=&contact_id=&date_from=&date_to=] */
export async function listPayments(
  params?: {
    status?: PaymentStatus;
    contact_id?: number;
    date_from?: string;
    date_to?: string;
  },
  signal?: AbortSignal,
): Promise<ListPaymentsResponse> {
  const { data } = await api.get<ListPaymentsResponse>(
    `${ACCOUNTING_BASE}/payments`,
    {
      params: {
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.contact_id ? { contact_id: params.contact_id } : {}),
        ...(params?.date_from ? { date_from: params.date_from } : {}),
        ...(params?.date_to ? { date_to: params.date_to } : {}),
      },
      signal,
    },
  );
  return data;
}

/** POST /accounting/payments — create a draft payment with allocations. */
export async function createPayment(
  body: CreatePaymentRequest,
): Promise<Payment> {
  const { data } = await api.post<Payment>(
    `${ACCOUNTING_BASE}/payments`,
    body,
  );
  return data;
}

/** GET /accounting/payments/{id} */
export async function getPayment(
  id: number,
  signal?: AbortSignal,
): Promise<Payment> {
  const { data } = await api.get<Payment>(
    `${ACCOUNTING_BASE}/payments/${id}`,
    { signal },
  );
  return data;
}

/** PATCH /accounting/payments/{id} — DRAFT only. */
export async function updatePayment(
  id: number,
  body: UpdatePaymentRequest,
): Promise<Payment> {
  const { data } = await api.patch<Payment>(
    `${ACCOUNTING_BASE}/payments/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/payments/{id}/post — irreversible. Engine writes
 * Dr 2100 A/P / Cr wallet (amount - tds) / Cr 2210 TDS Payable (tds)
 * when tds_minor > 0. IT Act Chapter XVII-B requires tds_section
 * whenever tds_minor > 0; the handler 422s with field='tds_section'.
 */
export async function postPayment(id: number): Promise<PostedPayment> {
  const { data } = await api.post<PostedPayment>(
    `${ACCOUNTING_BASE}/payments/${id}/post`,
    {},
  );
  return data;
}

/** POST /accounting/payments/{id}/void — mirror of receipts void. */
export async function voidPayment(id: number): Promise<Payment> {
  const { data } = await api.post<Payment>(
    `${ACCOUNTING_BASE}/payments/${id}/void`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// G_ACC8 — Reports (read-only). Five JSON endpoints + four CSV variants.
//
// All reports take an optional ?as_of=YYYY-MM-DD (default = today UTC on the
// server). Profit-and-loss takes ?from=&to= instead. CSV variants live at
// /reports/<name>.csv with the same query params; the backend streams the
// CSV directly so we route via the raw URL builders below — fetch the
// browser-side download via a normal anchor `download` attribute or push
// the user at /reports/<name>.csv?as_of=... directly.
//
// No CSRF on these reads. Backend role chain: authMW → acctFeature →
// accountingRole → handler.
// ---------------------------------------------------------------------------

export const ACCOUNTING_REPORTS_BASE = `${ACCOUNTING_BASE}/reports`;

/** GET /accounting/reports/trial-balance[?as_of=YYYY-MM-DD] */
export async function getTrialBalance(
  asOf?: string,
  signal?: AbortSignal,
): Promise<TrialBalanceResult> {
  const { data } = await api.get<TrialBalanceResult>(
    `${ACCOUNTING_REPORTS_BASE}/trial-balance`,
    { params: asOf ? { as_of: asOf } : undefined, signal },
  );
  return data;
}

/** GET /accounting/reports/profit-and-loss?from=YYYY-MM-DD&to=YYYY-MM-DD */
export async function getProfitAndLoss(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<ProfitAndLossResult> {
  const { data } = await api.get<ProfitAndLossResult>(
    `${ACCOUNTING_REPORTS_BASE}/profit-and-loss`,
    { params: { from, to }, signal },
  );
  return data;
}

/** GET /accounting/reports/balance-sheet[?as_of=YYYY-MM-DD] */
export async function getBalanceSheet(
  asOf?: string,
  signal?: AbortSignal,
): Promise<BalanceSheetResult> {
  const { data } = await api.get<BalanceSheetResult>(
    `${ACCOUNTING_REPORTS_BASE}/balance-sheet`,
    { params: asOf ? { as_of: asOf } : undefined, signal },
  );
  return data;
}

/** GET /accounting/reports/msme-overdue[?as_of=YYYY-MM-DD] */
export async function getMSMEOverdue(
  asOf?: string,
  signal?: AbortSignal,
  bankRateBps?: number,
): Promise<MSMEOverdueResult> {
  // bank_rate_bps — the RBI Bank Rate for the MSMED §16 estimate
  // (omitted → the server's default).
  const { data } = await api.get<MSMEOverdueResult>(
    `${ACCOUNTING_REPORTS_BASE}/msme-overdue`,
    {
      params: {
        ...(asOf ? { as_of: asOf } : {}),
        ...(bankRateBps ? { bank_rate_bps: bankRateBps } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * GET /accounting/reports/tds-receivable[?from=&to=] — customer TDS on
 * posted receipts in the range (default: the current Indian FY), per
 * deduction + 26AS-style groups, with the 1400 ledger alongside.
 */
export async function getTDSReceivable(
  range: { from?: string; to?: string },
  signal?: AbortSignal,
): Promise<TDSReceivableResult> {
  const { data } = await api.get<TDSReceivableResult>(
    `${ACCOUNTING_REPORTS_BASE}/tds-receivable`,
    {
      params: {
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * GET /accounting/reports/tds-receivable.csv[?from=&to=] — the same rows
 * as CSV, fetched as a blob through the authed client (the active entity
 * header rides along); blobErrorToApi keeps the server's message.
 */
export async function downloadTDSReceivableCSV(range: {
  from?: string;
  to?: string;
}): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${ACCOUNTING_REPORTS_BASE}/tds-receivable.csv`,
      {
        params: {
          ...(range.from ? { from: range.from } : {}),
          ...(range.to ? { to: range.to } : {}),
        },
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** GET /accounting/reports/dashboard[?as_of=YYYY-MM-DD] */
export async function getAccountingDashboard(
  asOf?: string,
  signal?: AbortSignal,
): Promise<DashboardWidget> {
  const { data } = await api.get<DashboardWidget>(
    `${ACCOUNTING_REPORTS_BASE}/dashboard`,
    { params: asOf ? { as_of: asOf } : undefined, signal },
  );
  return data;
}

/**
 * CSV download URL builders. The CSV endpoints stream attachment-disposed
 * text directly; the page links these into an `<a download href={...}>` so
 * the browser writes the file (axios round-tripping would lose the
 * Content-Disposition filename). Backend route shape:
 *   GET /api/v1/accounting/reports/<name>.csv?as_of=...
 *
 * AUDIT G_ACC8 HIGH-1 fix: previously emitted origin-relative URLs
 * (path only). The browser resolves <a href> against the current
 * origin — which is the frontend, not the backend — so the CSV
 * Export buttons 404'd on every cross-origin deploy (staging:
 * staging.workright.in → backend-staging-eedb.up.railway.app; local:
 * :3000 → :8090). Prefix API_BASE_URL so the anchor hits the API
 * directly. SameSite=None on the auth cookies (commit 527e5d9) lets
 * them flow on the cross-site GET; CSRF is not required on reads.
 */
const csvAbs = (path: string) => `${API_BASE_URL}${path}`;

export function trialBalanceCSVURL(asOf?: string): string {
  return csvAbs(
    asOf
      ? `${ACCOUNTING_REPORTS_BASE}/trial-balance.csv?as_of=${encodeURIComponent(asOf)}`
      : `${ACCOUNTING_REPORTS_BASE}/trial-balance.csv`,
  );
}

export function profitAndLossCSVURL(from: string, to: string): string {
  return csvAbs(
    `${ACCOUNTING_REPORTS_BASE}/profit-and-loss.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
  );
}

export function balanceSheetCSVURL(asOf?: string): string {
  return csvAbs(
    asOf
      ? `${ACCOUNTING_REPORTS_BASE}/balance-sheet.csv?as_of=${encodeURIComponent(asOf)}`
      : `${ACCOUNTING_REPORTS_BASE}/balance-sheet.csv`,
  );
}

export function msmeOverdueCSVURL(asOf?: string, bankRateBps?: number): string {
  const q = new URLSearchParams();
  if (asOf) q.set("as_of", asOf);
  if (bankRateBps) q.set("bank_rate_bps", String(bankRateBps));
  const qs = q.toString();
  return csvAbs(`${ACCOUNTING_REPORTS_BASE}/msme-overdue.csv${qs ? `?${qs}` : ""}`);
}

// ---------------------------------------------------------------------------
// G_ACC9 — audit-events viewer + period_locks admin.
// ---------------------------------------------------------------------------

const ACCOUNTING_AUDIT_EVENTS_BASE = `${ACCOUNTING_BASE}/audit-events`;
const ACCOUNTING_PERIOD_LOCKS_BASE = `${ACCOUNTING_BASE}/period-locks`;

/**
 * GET /api/v1/accounting/audit-events
 *   ?entity_type=&entity_id=&actor_id=&action=&from=&to=&limit=&offset=
 *
 * Read-only — no CSRF. Tenant-scoped at the server via session cookie.
 * The backend caps `limit` server-side (100); pass a larger value and
 * it'll be silently clamped. Filters compose with AND.
 */
export async function listAuditEvents(
  params?: ListAuditEventsParams,
  signal?: AbortSignal,
): Promise<ListAuditEventsResponse> {
  const { data } = await api.get<ListAuditEventsResponse>(
    ACCOUNTING_AUDIT_EVENTS_BASE,
    { params, signal },
  );
  return data;
}

/**
 * GET /api/v1/accounting/period-locks
 *
 * Returns every lock in the tenant, newest-first by period_end.
 */
export async function listPeriodLocks(
  signal?: AbortSignal,
): Promise<ListPeriodLocksResponse> {
  const { data } = await api.get<ListPeriodLocksResponse>(
    ACCOUNTING_PERIOD_LOCKS_BASE,
    { signal },
  );
  return data;
}

/**
 * POST /api/v1/accounting/period-locks
 *
 * Locks one period. Idempotent at the engine layer — re-locking the
 * same period_end is a no-op (no duplicate audit row). The handler
 * still returns 201 with the canonical row.
 */
export async function createPeriodLock(
  body: CreatePeriodLockRequest,
): Promise<PeriodLock> {
  const { data } = await api.post<PeriodLock>(
    ACCOUNTING_PERIOD_LOCKS_BASE,
    body,
  );
  return data;
}

/**
 * DELETE /api/v1/accounting/period-locks/{period_end}[?reason=...]
 *
 * IRREVERSIBLE. Gated server-side on:
 *   - RequireCapability("override_lock")   → 403 capability_missing
 *   - RequireTOTPEnrolledAndVerified       → 403 totp_enrollment_required
 *                                            or 401 totp_required
 * The caller must surface those error codes (route to /2fa-setup or
 * /2fa-verify respectively) — mirrors the offboarding /close + payroll
 * /lock patterns.
 *
 * `reason` is an optional unlock note the engine stamps into the
 * audit_events.after JSONB. Surface it on the confirmation dialog so
 * the auditor knows WHY the lock was lifted (Rule 3(1)).
 */
export async function deletePeriodLock(
  periodEnd: string,
  reason?: string,
): Promise<void> {
  const params = reason ? { reason } : undefined;
  await api.delete(`${ACCOUNTING_PERIOD_LOCKS_BASE}/${periodEnd}`, { params });
}

// ---------------------------------------------------------------------------
// G_ACC_CR2 — Opening Balance CSV importer.
//
// Five endpoints under /api/v1/accounting/opening-balance/* — see
// backend/internal/handlers/accounting_opening_balance.go for the canonical
// surface and the per-endpoint middleware chain:
//
//   POST /preview        — multipart {file, as_of_date}; 200 on clean,
//                          422 + same body shape on any per-row issue.
//   POST /commit         — multipart {file, as_of_date}; STEP-UP TOTP
//                          required server-side. 201 on fresh commit,
//                          200 + already_imported on idempotent retry.
//   GET  /imports        — audit ledger: every opening-balance commit
//                          for the tenant, newest first.
//   GET  /imports/{id}   — single row (audit-trail viewer).
//   GET  /template.csv   — downloads the canonical 4-column template.
//
// Tenant scoping is handled server-side via session; no tenant_id is
// ever sent on the wire.
//
// Wire sentinels worth handling specially:
//   - 422 validation        — preview shape with validation_errors[]
//   - 422 empty_csv         — file was zero bytes / blank
//   - 422 invalid_header    — header row doesn't match expected columns
//   - 422 too_many_rows     — > 5,000 data rows
//   - 422 unbalanced        — sum(debits) != sum(credits)
//   - 409 period_locked     — a period_lock at/after as_of_date exists
//   - 409 prior_import_voided — earlier import for same hash+date was
//                               voided; unlock period + re-upload
//   - 413 file_too_large    — over 5 MiB cap
// ---------------------------------------------------------------------------

import type {
  ListOpeningBalanceImportsResponse,
  OpeningBalanceCommitResponse,
  OpeningBalanceImport,
  OpeningBalancePreviewResult,
} from "@/types/opening-balance";

const ACCOUNTING_OPENING_BALANCE_BASE = `${ACCOUNTING_BASE}/opening-balance`;

/**
 * POST /api/v1/accounting/opening-balance/preview — multipart upload.
 *
 * The backend returns 200 on a clean preview AND 422 on any per-row
 * validation issue, both with the SAME PreviewResult body shape. This
 * helper unwraps the 422 path so callers always see the structured
 * preview without throwing — letting the wizard render the per-row
 * error list inline. Genuine network / 5xx errors still throw.
 *
 * Pre-flight 413 (over 5 MiB cap) + 422 empty_csv / invalid_header /
 * too_many_rows still throw — they're whole-file rejects with no
 * useful preview body and should surface as toasts.
 */
export async function previewOpeningBalance(
  file: File,
  asOfDate: string,
): Promise<OpeningBalancePreviewResult> {
  const form = new FormData();
  form.append("file", file);
  form.append("as_of_date", asOfDate);
  try {
    const { data } = await api.post<OpeningBalancePreviewResult>(
      `${ACCOUNTING_OPENING_BALANCE_BASE}/preview`,
      form,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return data;
  } catch (err) {
    // 422 + a structured PreviewResult body — unwrap so the caller
    // gets the rows + errors to render inline. The shape check is
    // duck-typed because the backend's error envelope for 422
    // validation IS the preview body (handler writes it via
    // encodeJSON without the {error,message} wrapper).
    if (
      typeof err === "object" &&
      err !== null &&
      "response" in err &&
      typeof (err as { response?: unknown }).response === "object"
    ) {
      const response = (err as { response?: { status?: number; data?: unknown } })
        .response;
      const body = response?.data;
      if (
        response?.status === 422 &&
        body !== null &&
        typeof body === "object" &&
        "rows" in body &&
        "validation_errors" in body
      ) {
        return body as OpeningBalancePreviewResult;
      }
    }
    throw err;
  }
}

/**
 * POST /api/v1/accounting/opening-balance/commit — multipart upload.
 *
 * Step-up TOTP gated server-side; the caller must intercept the
 * `totp_required` / `totp_enrollment_required` sentinels and route to
 * /2fa-verify or /2fa-setup respectively (mirrors period-locks unlock
 * + payroll lock patterns).
 *
 * On idempotent retry (same file_hash + as_of_date within 1 h) the
 * server returns 200 + `already_imported: true` with the existing
 * journal_entry_id + entry_no — the UI should celebrate as success.
 */
export async function commitOpeningBalance(
  file: File,
  asOfDate: string,
): Promise<OpeningBalanceCommitResponse> {
  const form = new FormData();
  form.append("file", file);
  form.append("as_of_date", asOfDate);
  const { data } = await api.post<OpeningBalanceCommitResponse>(
    `${ACCOUNTING_OPENING_BALANCE_BASE}/commit`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data;
}

/**
 * GET /api/v1/accounting/opening-balance/imports — list every prior
 * commit for the tenant, newest first. Read-only audit-trail viewer.
 * No pagination (a tenant typically has one or a handful of imports).
 */
export async function listOpeningBalanceImports(
  signal?: AbortSignal,
): Promise<ListOpeningBalanceImportsResponse> {
  const { data } = await api.get<ListOpeningBalanceImportsResponse>(
    `${ACCOUNTING_OPENING_BALANCE_BASE}/imports`,
    { signal },
  );
  return data;
}

/**
 * GET /api/v1/accounting/opening-balance/imports/{id} — one row.
 * Cross-tenant id is 404 not_found (no oracle).
 */
export async function getOpeningBalanceImport(
  id: number,
  signal?: AbortSignal,
): Promise<OpeningBalanceImport> {
  const { data } = await api.get<OpeningBalanceImport>(
    `${ACCOUNTING_OPENING_BALANCE_BASE}/imports/${id}`,
    { signal },
  );
  return data;
}

/**
 * Absolute URL for the GET /template.csv endpoint. The CSV streams as
 * an attachment-disposed download, so the wizard hands this to an
 * `<a download href={...}>` — axios round-tripping would lose the
 * Content-Disposition filename (mirrors the G_ACC8 reports CSV
 * download pattern). API_BASE_URL prefix ensures the anchor hits the
 * BACKEND origin in cross-origin deploys (staging, future production
 * across subdomains).
 */
export const openingBalanceTemplateURL = (): string =>
  `${API_BASE_URL}${ACCOUNTING_OPENING_BALANCE_BASE}/template.csv`;

// ---------------------------------------------------------------------------
// Tax codes (G_ACC3 — accounting_tax_codes master CRUD).
// 5 endpoints: POST / GET list / GET one / PATCH / POST archive.
// ---------------------------------------------------------------------------

export const ACCOUNTING_TAX_CODES_BASE = `${ACCOUNTING_BASE}/tax-codes`;

/**
 * GET /accounting/tax-codes[?kind=&include_inactive=]
 *
 * Default: active only. Pass `include_inactive: true` to see archived rows
 * (for the "Include archived" toggle).
 */
export async function listTaxCodes(
  params?: {
    kind?: TaxCodeKind;
    include_inactive?: boolean;
  },
  signal?: AbortSignal,
): Promise<ListTaxCodesResponse> {
  const { data } = await api.get<ListTaxCodesResponse>(
    ACCOUNTING_TAX_CODES_BASE,
    {
      params: {
        ...(params?.kind ? { kind: params.kind } : {}),
        ...(params?.include_inactive ? { include_inactive: "true" } : {}),
      },
      signal,
    },
  );
  return data;
}

/**
 * POST /accounting/tax-codes — create a tax code.
 *
 * Wire errors (mapped to field-level via handleAuthError):
 *   - 409 code_taken — field "code"
 *   - 422 validation — field-mapped per the server shape constraints
 */
export async function createTaxCode(
  body: CreateTaxCodeRequest,
): Promise<TaxCode> {
  const { data } = await api.post<TaxCode>(ACCOUNTING_TAX_CODES_BASE, body);
  return data;
}

/**
 * GET /accounting/tax-codes/{id} — load one tax code. Cross-tenant id
 * is 404 not_found (no oracle). Includes inactive rows (historical
 * resolver semantics).
 */
export async function getTaxCode(
  id: number,
  signal?: AbortSignal,
): Promise<TaxCode> {
  const { data } = await api.get<TaxCode>(
    `${ACCOUNTING_TAX_CODES_BASE}/${id}`,
    { signal },
  );
  return data;
}

/**
 * PATCH /accounting/tax-codes/{id} — partial update (kind is NOT
 * updateable). Writes master_update audit_events row in the same TX.
 */
export async function updateTaxCode(
  id: number,
  body: UpdateTaxCodeRequest,
): Promise<TaxCode> {
  const { data } = await api.patch<TaxCode>(
    `${ACCOUNTING_TAX_CODES_BASE}/${id}`,
    body,
  );
  return data;
}

/**
 * POST /accounting/tax-codes/{id}/archive — soft-archive (is_active=FALSE).
 * No hard delete (Companies Act §128(5) + posted-journal integrity).
 * Idempotent.
 */
export async function archiveTaxCode(id: number): Promise<TaxCode> {
  const { data } = await api.post<TaxCode>(
    `${ACCOUNTING_TAX_CODES_BASE}/${id}/archive`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// G_ACC7 — Tax returns (GST + TDS).
// ---------------------------------------------------------------------------

export const ACCOUNTING_TAX_RETURNS_BASE = `${ACCOUNTING_BASE}/tax-returns`;

/**
 * GET /accounting/tax-returns[?return_type=&year=]
 *
 * Lists all generated returns for the tenant. Snapshot is omitted in
 * list items (cost); use getTaxReturn() to get the full snapshot.
 */
export async function listTaxReturns(params?: {
  return_type?: import("@/types/accounting").TaxReturnType;
  year?: number;
}, signal?: AbortSignal): Promise<import("@/types/accounting").ListTaxReturnsResponse> {
  const { data } = await api.get<import("@/types/accounting").ListTaxReturnsResponse>(
    ACCOUNTING_TAX_RETURNS_BASE,
    { params, signal },
  );
  return data;
}

/**
 * GET /accounting/tax-returns/{id}
 *
 * Returns the full DTO including the snapshot_jsonb field.
 */
export async function getTaxReturn(
  id: number,
  signal?: AbortSignal,
): Promise<import("@/types/accounting").TaxReturn> {
  const { data } = await api.get<import("@/types/accounting").TaxReturn>(
    `${ACCOUNTING_TAX_RETURNS_BASE}/${id}`,
    { signal },
  );
  return data;
}

/**
 * POST /accounting/tax-returns — generate a new return.
 *
 * 201 Created on success. 409 already_exists if the (type, year, index)
 * tuple is already taken. 422 validation on invalid period_index for the
 * chosen cadence.
 */
export async function generateTaxReturn(
  body: import("@/types/accounting").GenerateTaxReturnRequest,
): Promise<import("@/types/accounting").TaxReturn> {
  const { data } = await api.post<import("@/types/accounting").TaxReturn>(
    ACCOUNTING_TAX_RETURNS_BASE,
    body,
  );
  return data;
}

/**
 * Download URL for GET /accounting/tax-returns/{id}/csv.
 *
 * Returns the URL string so callers can open it directly (window.open /
 * <a href={...} download>). The server lazy-caches the CSV in the DB on
 * first download. Cookie auth is passed automatically by the browser.
 */
export function taxReturnCSVURL(id: number): string {
  return `${API_BASE_URL}${ACCOUNTING_TAX_RETURNS_BASE}/${id}/csv`;
}

// ---------------------------------------------------------------------------
// Manual journal entries (Sprint 4 §C).
// ---------------------------------------------------------------------------

export const ACCOUNTING_JOURNAL_ENTRIES_BASE = `${ACCOUNTING_BASE}/journal-entries`;

/**
 * POST /accounting/journal-entries — post a balanced manual journal.
 *
 * CSRF-guarded write (the shared axios interceptor adds the header).
 * 201 returns the persisted entry incl. entry_no + posted_at + lines.
 *
 * Wire errors (caller maps via handleAuthError):
 *   - 400 / 422 validation — `{ field }`: unbalanced, < 2 lines, etc.
 *   - 400 invalid_account — a line's account_id isn't in the tenant CoA
 *   - 409 period_locked — `{ field: "date" }` (Companies Act §128(5))
 *   - 422 entry_unbalanced — sum(debit) != sum(credit)
 *   - 422 line_xor — a line set both / neither of debit & credit
 */
export async function createManualJournal(
  body: import("@/types/accounting").CreateManualJournalRequest,
): Promise<import("@/types/accounting").JournalEntry> {
  const { data } = await api.post<import("@/types/accounting").JournalEntry>(
    ACCOUNTING_JOURNAL_ENTRIES_BASE,
    body,
  );
  return data;
}

/**
 * GET /accounting/journal-entries[?source_type=&date_from=&date_to=]
 *
 * Lists posted journal entries (manual + engine-sourced), tenant- +
 * entity-scoped via session. Optional filters compose with AND:
 *   - source_type — restrict to one origin ("manual"/"invoice"/...);
 *     the backend 400s on an unrecognised value.
 *   - date_from / date_to — YYYY-MM-DD inclusive bounds on the entry date.
 *
 * Read-only — no CSRF. The header rows carry no money; per-entry totals
 * come from getJournalEntry().
 */
export async function listJournalEntries(
  params?: {
    source_type?: string;
    date_from?: string;
    date_to?: string;
  },
  signal?: AbortSignal,
): Promise<import("@/types/accounting").ListJournalEntriesResponse> {
  const { data } = await api.get<
    import("@/types/accounting").ListJournalEntriesResponse
  >(ACCOUNTING_JOURNAL_ENTRIES_BASE, {
    params: {
      ...(params?.source_type ? { source_type: params.source_type } : {}),
      ...(params?.date_from ? { date_from: params.date_from } : {}),
      ...(params?.date_to ? { date_to: params.date_to } : {}),
    },
    signal,
  });
  return data;
}

/**
 * GET /accounting/journal-entries/{id} — the header + lines (with resolved
 * account code/name + contact name) + computed total_debit / total_credit.
 * Cross-tenant or missing id is 404 not_found (no oracle).
 */
export async function getJournalEntry(
  id: number,
  signal?: AbortSignal,
): Promise<import("@/types/accounting").JournalEntryDetail> {
  const { data } = await api.get<import("@/types/accounting").JournalEntryDetail>(
    `${ACCOUNTING_JOURNAL_ENTRIES_BASE}/${id}`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// CR0.2 — Tally XML export (ledger-name mappings + voucher export).
// ---------------------------------------------------------------------------

export const ACCOUNTING_TALLY_MAPPINGS_BASE = `${ACCOUNTING_BASE}/tally-mappings`;

/**
 * GET /accounting/tally-mappings — every mappable ledger for the tenant:
 * the payroll pseudo-ledgers (kind="pseudo") followed by the chart of
 * accounts (kind="account"). Each row carries the EFFECTIVE
 * tally_ledger_name (mapping if set, else default) so the settings page
 * renders the export's actual output. Read-only — no CSRF.
 */
export async function listTallyMappings(
  signal?: AbortSignal,
): Promise<ListTallyMappingsResponse> {
  const { data } = await api.get<ListTallyMappingsResponse>(
    ACCOUNTING_TALLY_MAPPINGS_BASE,
    { signal },
  );
  return data;
}

/**
 * PUT /accounting/tally-mappings — upsert one row's mapping. Exactly one
 * of account_id / pseudo_code must be set. Sending an empty
 * tally_ledger_name CLEARS the mapping (the backend reverts to the
 * default name). Returns the updated resolved row (unwrapped from the
 * `{ item }` envelope) so the caller can reset local row state without
 * waiting for the list refetch.
 */
export async function upsertTallyMapping(
  body: UpsertTallyMappingRequest,
): Promise<TallyMappingRow> {
  const { data } = await api.put<{ item: TallyMappingRow }>(
    ACCOUNTING_TALLY_MAPPINGS_BASE,
    body,
  );
  return data.item;
}

/**
 * GET /accounting/exports/tally.xml?from=&to= — TallyPrime-importable
 * voucher XML for every posted journal in the (inclusive) date range.
 * Both params are required; the backend 422s on a missing / invalid /
 * inverted range. Fetched as a blob via the authed client (same pattern
 * as payroll downloadCSV); blobErrorToApi rehydrates the JSON envelope
 * so a 422 keeps its sentinel message instead of degrading to a
 * blob-parse generic.
 *
 * includePayroll — also export the payroll salary journals (left out by
 * default: each payroll run's own Tally JV carries the same voucher, and
 * importing both books the salary twice).
 */
export async function downloadTallyXML(
  from: string,
  to: string,
  includePayroll = false,
  partyLedgers = false,
): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${ACCOUNTING_BASE}/exports/tally.xml`,
      {
        // party_ledgers=1: receivable / payable lines post to each
        // customer's / vendor's own ledger (QA books #12).
        params: {
          from,
          to,
          payroll: includePayroll ? "include" : "exclude",
          ...(partyLedgers ? { party_ledgers: 1 } : {}),
        },
        responseType: "blob",
      },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/**
 * GET /accounting/journal-entries/{id}/tally.xml — ONE posted journal as a
 * TallyPrime-importable voucher. Fetched as a blob via the authed client
 * (cookie auth + the active entity header ride along); blobErrorToApi
 * keeps the server's sentinel message on a 404 / 422. The server names the
 * file tally-voucher-<entry_no>.xml; the caller saves under that same name.
 *
 * partyLedgers — the date-range export's "one ledger per customer and
 * vendor" option (party_ledgers=1), which the single voucher used to
 * ignore (2026-10 integrator follow-up).
 */
export async function downloadJournalTallyXML(id: number, partyLedgers = false): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${ACCOUNTING_JOURNAL_ENTRIES_BASE}/${id}/tally.xml`,
      { params: partyLedgers ? { party_ledgers: 1 } : undefined, responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** PATCH /accounting/gst-profile — set the company GSTIN / GST state. */
export async function updateGstProfile(body: UpdateGstProfileRequest): Promise<GstProfile> {
  const { data } = await api.patch<GstProfile>(`${ACCOUNTING_BASE}/gst-profile`, body);
  return data;
}

/** GET /accounting/tax-returns/preview — the numbers Generate would freeze; nothing saved. */
export async function previewTaxReturn(
  params: { return_type: TaxReturnType; period_year: number; period_index: number },
  signal?: AbortSignal,
): Promise<TaxReturnPreview> {
  const { data } = await api.get<TaxReturnPreview>(`${ACCOUNTING_BASE}/tax-returns/preview`, {
    params,
    signal,
  });
  return data;
}

/** GET /accounting/bills/tds-preview — the TDS rate + amount a bill under
 *  `section` would withhold (§206AA higher rate when the vendor has no PAN). */
export async function previewBillTDS(
  params: { section: string; subtotal_minor: number; contact_id?: number },
  signal?: AbortSignal,
): Promise<BillTDSPreview> {
  const { data } = await api.get<BillTDSPreview>(`${ACCOUNTING_BASE}/bills/tds-preview`, { params, signal });
  return data;
}

/** GET /accounting/accounts/{id}/ledger — opening, lines, running and closing balance. */
export async function getAccountLedger(
  id: number,
  params: { from?: string; to?: string },
  signal?: AbortSignal,
): Promise<AccountLedger> {
  const { data } = await api.get<AccountLedger>(`${ACCOUNTING_BASE}/accounts/${id}/ledger`, {
    params,
    signal,
  });
  return data;
}

/** GET /accounting/accounts/{id}/ledger?format=csv — the same ledger as a CSV file. */
export async function downloadAccountLedgerCSV(id: number, params: { from?: string; to?: string }): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(`${ACCOUNTING_BASE}/accounts/${id}/ledger`, {
      params: { ...params, format: "csv" },
      responseType: "blob",
    });
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

/** GET /accounting/payroll-runs — locked payroll runs with totals (read-only). */
export async function listPayrollRegister(signal?: AbortSignal): Promise<ListPayrollRegisterResponse> {
  const { data } = await api.get<ListPayrollRegisterResponse>(`${ACCOUNTING_BASE}/payroll-runs`, { signal });
  return data;
}

/** GET /accounting/payroll-runs/{id} — one locked run's register (read-only). */
export async function getPayrollRegister(id: number, signal?: AbortSignal): Promise<PayrollRegisterRun> {
  const { data } = await api.get<PayrollRegisterRun>(`${ACCOUNTING_BASE}/payroll-runs/${id}`, { signal });
  return data;
}

/** GET /accounting/gst-profile — company GST state + GSTIN (read-only). */
export async function getGstProfile(signal?: AbortSignal): Promise<GstProfile> {
  const { data } = await api.get<GstProfile>(`${ACCOUNTING_BASE}/gst-profile`, {
    signal,
  });
  return data;
}
